import React, { useState, useEffect } from "react";
import { Truck, Plus, Edit2, Trash2, Save, X, TrendingUp, Package, DollarSign, BarChart3 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminCargoPage = () => {
  const [activeTab, setActiveTab] = useState("settings");
  const [settings, setSettings] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [reports, setReports] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingSettings, setEditingSettings] = useState(null);
  const [showDesiEditor, setShowDesiEditor] = useState(false);
  const [showBaremEditor, setShowBaremEditor] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [settingsRes, companiesRes, reportsRes] = await Promise.all([
        axios.get(`${API}/admin/cargo/settings`),
        axios.get(`${API}/admin/cargo/companies`),
        axios.get(`${API}/admin/cargo/reports`)
      ]);
      setSettings(settingsRes.data);
      setCompanies(companiesRes.data);
      setReports(reportsRes.data);
    } catch (error) {
      toast.error("Veriler yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat("tr-TR", { style: "currency", currency: "TRY" }).format(price);
  };

  const handleSaveSettings = async () => {
    if (!editingSettings) return;
    
    try {
      if (editingSettings.id) {
        await axios.put(`${API}/admin/cargo/settings/${editingSettings.id}`, editingSettings);
        toast.success("Ayarlar güncellendi");
      } else {
        await axios.post(`${API}/admin/cargo/settings`, editingSettings);
        toast.success("Ayarlar oluşturuldu");
      }
      setEditingSettings(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Kayıt başarısız");
    }
  };

  const handleDeleteSettings = async (id) => {
    if (!window.confirm("Bu ayarı silmek istediğinize emin misiniz?")) return;
    
    try {
      await axios.delete(`${API}/admin/cargo/settings/${id}`);
      toast.success("Ayar silindi");
      fetchData();
    } catch (error) {
      toast.error("Silme başarısız");
    }
  };

  const addDesiRow = () => {
    if (!editingSettings) return;
    const lastDesi = editingSettings.desi_prices[editingSettings.desi_prices.length - 1];
    const newRow = {
      min_desi: lastDesi ? lastDesi.max_desi : 0,
      max_desi: lastDesi ? lastDesi.max_desi + 5 : 5,
      price: lastDesi ? lastDesi.price + 50 : 50
    };
    setEditingSettings({
      ...editingSettings,
      desi_prices: [...editingSettings.desi_prices, newRow]
    });
  };

  const addBaremRow = () => {
    if (!editingSettings) return;
    const lastBarem = editingSettings.barem_prices[editingSettings.barem_prices.length - 1];
    const newRow = {
      min_order_amount: lastBarem ? lastBarem.max_order_amount : 0,
      max_order_amount: lastBarem ? lastBarem.max_order_amount + 50 : 50,
      price: lastBarem ? lastBarem.price + 10 : 30
    };
    setEditingSettings({
      ...editingSettings,
      barem_prices: [...editingSettings.barem_prices, newRow]
    });
  };

  const updateDesiRow = (index, field, value) => {
    const newPrices = [...editingSettings.desi_prices];
    newPrices[index][field] = parseFloat(value) || 0;
    setEditingSettings({ ...editingSettings, desi_prices: newPrices });
  };

  const updateBaremRow = (index, field, value) => {
    const newPrices = [...editingSettings.barem_prices];
    newPrices[index][field] = parseFloat(value) || 0;
    setEditingSettings({ ...editingSettings, barem_prices: newPrices });
  };

  const deleteDesiRow = (index) => {
    const newPrices = editingSettings.desi_prices.filter((_, i) => i !== index);
    setEditingSettings({ ...editingSettings, desi_prices: newPrices });
  };

  const deleteBaremRow = (index) => {
    const newPrices = editingSettings.barem_prices.filter((_, i) => i !== index);
    setEditingSettings({ ...editingSettings, barem_prices: newPrices });
  };

  if (loading) {
    return <div className="p-8 text-center">Yükleniyor...</div>;
  }

  return (
    <div className="min-h-screen bg-slate-100 py-8">
      <div className="container mx-auto px-4 max-w-6xl">
        <div className="flex items-center gap-3 mb-8">
          <Truck className="h-8 w-8 text-orange-500" />
          <h1 className="text-2xl font-bold text-slate-900">Kargo Yönetimi</h1>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <Button variant={activeTab === "settings" ? "default" : "outline"} onClick={() => setActiveTab("settings")}>
            <Package className="h-4 w-4 mr-2" />Kargo Ayarları
          </Button>
          <Button variant={activeTab === "reports" ? "default" : "outline"} onClick={() => setActiveTab("reports")}>
            <BarChart3 className="h-4 w-4 mr-2" />Raporlar
          </Button>
        </div>

        {/* Reports Tab */}
        {activeTab === "reports" && reports && (
          <div className="grid md:grid-cols-4 gap-4 mb-8">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-blue-100 rounded-lg"><Package className="h-6 w-6 text-blue-600" /></div>
                  <div>
                    <p className="text-sm text-slate-500">Toplam Sipariş</p>
                    <p className="text-2xl font-bold">{reports.total_orders}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-orange-100 rounded-lg"><DollarSign className="h-6 w-6 text-orange-600" /></div>
                  <div>
                    <p className="text-sm text-slate-500">Toplam Kargo Maliyeti</p>
                    <p className="text-2xl font-bold">{formatPrice(reports.total_cargo_cost)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-green-100 rounded-lg"><TrendingUp className="h-6 w-6 text-green-600" /></div>
                  <div>
                    <p className="text-sm text-slate-500">Kargo Geliri</p>
                    <p className="text-2xl font-bold">{formatPrice(reports.total_cargo_revenue)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-purple-100 rounded-lg"><Truck className="h-6 w-6 text-purple-600" /></div>
                  <div>
                    <p className="text-sm text-slate-500">Ücretsiz Kargo</p>
                    <p className="text-2xl font-bold">{reports.free_shipping_orders}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Settings Tab */}
        {activeTab === "settings" && (
          <>
            {/* Current Settings */}
            {!editingSettings && (
              <div className="space-y-4">
                {settings.map((setting) => (
                  <Card key={setting.id}>
                    <CardHeader className="flex flex-row items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Truck className="h-5 w-5 text-orange-500" />
                        <CardTitle>{setting.cargo_company_name}</CardTitle>
                        {setting.is_active && <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded">Aktif</span>}
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" onClick={() => setEditingSettings(setting)}>
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="destructive" onClick={() => handleDeleteSettings(setting.id)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="grid md:grid-cols-3 gap-4 text-sm">
                        <div>
                          <span className="text-slate-500">Ücretsiz Kargo Eşiği:</span>
                          <span className="ml-2 font-medium">{formatPrice(setting.free_shipping_threshold)}</span>
                        </div>
                        <div>
                          <span className="text-slate-500">Barem Eşiği:</span>
                          <span className="ml-2 font-medium">{formatPrice(setting.barem_threshold)}</span>
                        </div>
                        <div>
                          <span className="text-slate-500">KDV Oranı:</span>
                          <span className="ml-2 font-medium">%{setting.kdv_rate}</span>
                        </div>
                      </div>
                      
                      {/* Desi Prices Summary */}
                      <div className="mt-4">
                        <p className="text-sm font-medium text-slate-700 mb-2">Desi Fiyatları ({setting.desi_prices?.length || 0} adet)</p>
                        <div className="flex flex-wrap gap-2">
                          {setting.desi_prices?.slice(0, 5).map((dp, i) => (
                            <span key={i} className="px-2 py-1 bg-slate-100 rounded text-xs">
                              {dp.min_desi}-{dp.max_desi} desi: {formatPrice(dp.price)}
                            </span>
                          ))}
                          {setting.desi_prices?.length > 5 && (
                            <span className="px-2 py-1 bg-slate-200 rounded text-xs">+{setting.desi_prices.length - 5} daha</span>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}

                {settings.length === 0 && (
                  <Card>
                    <CardContent className="p-8 text-center">
                      <p className="text-slate-500">Henüz kargo ayarı yok</p>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}

            {/* Editor */}
            {editingSettings && (
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle>{editingSettings.id ? "Kargo Ayarını Düzenle" : "Yeni Kargo Ayarı"}</CardTitle>
                    <Button variant="ghost" size="sm" onClick={() => setEditingSettings(null)}><X className="h-4 w-4" /></Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Basic Settings */}
                  <div className="grid md:grid-cols-4 gap-4">
                    <div>
                      <Label>Ücretsiz Kargo Eşiği (TL)</Label>
                      <Input
                        type="number"
                        value={editingSettings.free_shipping_threshold}
                        onChange={(e) => setEditingSettings({...editingSettings, free_shipping_threshold: parseFloat(e.target.value)})}
                      />
                    </div>
                    <div>
                      <Label>Barem Eşiği (TL)</Label>
                      <Input
                        type="number"
                        value={editingSettings.barem_threshold}
                        onChange={(e) => setEditingSettings({...editingSettings, barem_threshold: parseFloat(e.target.value)})}
                      />
                    </div>
                    <div>
                      <Label>KDV Oranı (%)</Label>
                      <Input
                        type="number"
                        value={editingSettings.kdv_rate}
                        onChange={(e) => setEditingSettings({...editingSettings, kdv_rate: parseFloat(e.target.value)})}
                      />
                    </div>
                    <div className="flex items-center gap-2 pt-6">
                      <Switch
                        checked={editingSettings.is_active}
                        onCheckedChange={(checked) => setEditingSettings({...editingSettings, is_active: checked})}
                      />
                      <Label>Aktif</Label>
                    </div>
                  </div>

                  {/* Desi Prices */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <Label className="text-base font-semibold">Desi Fiyatları (KDV Hariç)</Label>
                      <Button size="sm" variant="outline" onClick={addDesiRow}><Plus className="h-4 w-4 mr-1" />Satır Ekle</Button>
                    </div>
                    <div className="border rounded-lg overflow-hidden">
                      <table className="w-full text-sm">
                        <thead className="bg-slate-50">
                          <tr>
                            <th className="px-4 py-2 text-left">Min Desi</th>
                            <th className="px-4 py-2 text-left">Max Desi</th>
                            <th className="px-4 py-2 text-left">Fiyat (TL)</th>
                            <th className="px-4 py-2 w-10"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {editingSettings.desi_prices?.map((row, index) => (
                            <tr key={index} className="border-t">
                              <td className="px-4 py-2">
                                <Input type="number" value={row.min_desi} onChange={(e) => updateDesiRow(index, "min_desi", e.target.value)} className="h-8" />
                              </td>
                              <td className="px-4 py-2">
                                <Input type="number" value={row.max_desi} onChange={(e) => updateDesiRow(index, "max_desi", e.target.value)} className="h-8" />
                              </td>
                              <td className="px-4 py-2">
                                <Input type="number" value={row.price} onChange={(e) => updateDesiRow(index, "price", e.target.value)} className="h-8" />
                              </td>
                              <td className="px-4 py-2">
                                <Button size="sm" variant="ghost" onClick={() => deleteDesiRow(index)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Barem Prices */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <Label className="text-base font-semibold">Barem Fiyatları (Düşük Tutarlı Siparişler - KDV Hariç)</Label>
                      <Button size="sm" variant="outline" onClick={addBaremRow}><Plus className="h-4 w-4 mr-1" />Satır Ekle</Button>
                    </div>
                    <div className="border rounded-lg overflow-hidden">
                      <table className="w-full text-sm">
                        <thead className="bg-slate-50">
                          <tr>
                            <th className="px-4 py-2 text-left">Min Tutar (TL)</th>
                            <th className="px-4 py-2 text-left">Max Tutar (TL)</th>
                            <th className="px-4 py-2 text-left">Kargo Fiyatı (TL)</th>
                            <th className="px-4 py-2 w-10"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {editingSettings.barem_prices?.map((row, index) => (
                            <tr key={index} className="border-t">
                              <td className="px-4 py-2">
                                <Input type="number" value={row.min_order_amount} onChange={(e) => updateBaremRow(index, "min_order_amount", e.target.value)} className="h-8" />
                              </td>
                              <td className="px-4 py-2">
                                <Input type="number" value={row.max_order_amount} onChange={(e) => updateBaremRow(index, "max_order_amount", e.target.value)} className="h-8" />
                              </td>
                              <td className="px-4 py-2">
                                <Input type="number" value={row.price} onChange={(e) => updateBaremRow(index, "price", e.target.value)} className="h-8" />
                              </td>
                              <td className="px-4 py-2">
                                <Button size="sm" variant="ghost" onClick={() => deleteBaremRow(index)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <div className="flex gap-2 pt-4">
                    <Button onClick={handleSaveSettings} className="bg-orange-500 hover:bg-orange-600">
                      <Save className="h-4 w-4 mr-2" />Kaydet
                    </Button>
                    <Button variant="outline" onClick={() => setEditingSettings(null)}>İptal</Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AdminCargoPage;
